'use client';

import { itemsPerQuery } from '@module/constants/pagenation';
import useGetLoginInfoQuery from '@module/helpers/auth/useGetLoginInfoQuery';
import { cn } from '@module/lib/utils';
import {
  useFanHideAddMutation,
  useFanHideDeleteMutation,
  useFetchLiveFanInfiniteQuery,
} from '@module/queries/live/liveQueryHooks';
import useModalStore from '@module/stores/useModalStore';
import { usePickBjLiveToggle } from '@module/stores/usePickBjLiveToggleStore';
import arrayToIdxObject from '@module/utils/arrayToIdxObject';
import catchAllErrors from '@module/utils/catchAllErrors';
import { BaseHttpResponseError } from '@module/utils/error/apiErrors';
import { useIntersectionObserver } from '@module/utils/useIntersectionObserver';
import SwitchButton from '@pc/components/atoms/button/SwitchButton';
import PickUserItem from '@pc/components/list/Pick/PickUserItem';
import PickUserList from '@pc/components/list/Pick/PickUserList';
import NotFoundDisplay from '@pc/components/result/NotFoundDisplay';
import { Button } from '@pc/components/ui/button';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

function FanPickContainer() {
  const router = useRouter();
  const { isLogin } = useGetLoginInfoQuery();
  const pushModal = useModalStore((state) => state.pushModal);
  const clearModals = useModalStore((state) => state.clearModals);
  const [isHideOnly, setIsHideOnly] = useState(false);
  const [isOnEditing, setIsOnEditing] = useState<boolean>(false);
  const [checkedIdxList, setCheckedIdxList] = useState<number[]>([]);

  const { isBjLive, toggleIsBjLive } = usePickBjLiveToggle();

  const getRankTitle = (rank: number) => {
    if (rank === 1) return '팬클럽 회장';

    return `열혈팬 ${rank}위`;
  };
  const { data, hasNextPage, fetchNextPage, refetch } = useFetchLiveFanInfiniteQuery(
    {
      limit: itemsPerQuery,
      isLive: isBjLive ? 'Y' : '',
      hideOnly: isHideOnly ? 'Y' : 'N',
    },
    {
      enabled: isLogin,
    },
  );

  const { observerRef } = useIntersectionObserver({
    isReadyToFetch: () => hasNextPage,
    onInView: fetchNextPage,
  });

  const { mutate: addFanHide } = useFanHideAddMutation({
    onSuccess: (data) => {
      pushModal({
        type: 'CONFIRM_MODAL',
        id: 'FanPickContainer',
        options: {
          description: data.message,
          onClose: clearModals,
        },
      });
      setCheckedIdxList([]);
      setIsOnEditing(false);
      refetch();
    },
    onError: (err) => {
      if (err instanceof BaseHttpResponseError) {
        catchAllErrors(err.message, () => {
          pushModal({
            type: 'CONFIRM_MODAL',
            id: 'FanPickContainer',
            options: {
              description: err.message,
            },
          });
        });
      }
    },
  });

  const { mutate: deleteFanHide } = useFanHideDeleteMutation({
    onSuccess: (data) => {
      pushModal({
        type: 'CONFIRM_MODAL',
        id: 'FanPickContainer',
        options: {
          description: data.message,
          onClose: clearModals,
        },
      });
      setCheckedIdxList([]);
      setIsOnEditing(false);
      setIsHideOnly(false);
      refetch();
    },
    onError: (err) => {
      if (err instanceof BaseHttpResponseError) {
        catchAllErrors(err.message, () => {
          pushModal({
            type: 'CONFIRM_MODAL',
            id: 'FanPickContainer',
            options: {
              description: err.message,
            },
          });
        });
      }
    },
  });

  const handleAllListClick = () => {
    setIsHideOnly(false);
    setIsOnEditing(false);
  };

  const handleHideListClick = () => {
    setIsHideOnly(!isHideOnly);
    setIsOnEditing(false);
  };

  const handleCheckAll = () => {
    if (!data) return;
    if (data.pages.length === checkedIdxList.length) {
      setCheckedIdxList([]);
    } else {
      setCheckedIdxList(data.pages.map((item) => item.userIdx));
    }
  };

  const handleHideClick = () => {
    if (checkedIdxList.length < 1) {
      setIsOnEditing(false);
      return;
    }
    if (isHideOnly) {
      deleteFanHide(arrayToIdxObject(checkedIdxList, 'toUserIdx'));
    } else {
      addFanHide(arrayToIdxObject(checkedIdxList, 'toUserIdx'));
    }
  };

  return (
    <>
      <section className='mb-[15px] flex items-center justify-between px-2'>
        <div className='flex items-center'>
          <button type='button' className='hover:underline' onClick={handleAllListClick}>
            <span className='text-[#777]'>{'전체'}</span>
            <span className='ml-1 font-bold text-baseFg'>{data?.total || 0}</span>
          </button>
          {data && data.hideCount > 0 && (
            <>
              <div className='mx-3 h-4 w-[1px] bg-zinc-300' />
              <button
                type='button'
                className={cn('text-baseFg hover:underline', {
                  'text-[#777]': !isHideOnly,
                })}
                onClick={handleHideListClick}
              >
                <span>{'숨김'}</span>
                <span className='ml-1 font-bold'>{data?.hideCount || 0}</span>
              </button>
            </>
          )}
          {!isOnEditing && !!data?.total && (
            <>
              <div className='mx-3 h-4 w-[1px] bg-zinc-300' />
              <button
                type='button'
                className='text-baseFg hover:underline'
                onClick={() => setIsOnEditing((prev) => !prev)}
              >
                {'편집'}
              </button>
            </>
          )}
          {isOnEditing && (
            <>
              <div className='mx-3 h-4 w-[1px] bg-zinc-300' />
              <button type='button' className='text-baseFg hover:underline' onClick={handleCheckAll}>
                {'전체선택'}
              </button>
              <div className='mx-3 h-4 w-[1px] bg-zinc-300' />
              <button type='button' className='text-baseFg hover:underline' onClick={handleHideClick}>
                {`${isHideOnly ? '해제' : '숨김'}(${checkedIdxList.length})`}
              </button>
            </>
          )}
        </div>

        <div className='flex items-center text-[16px] text-colorFg18'>
          {'방송중 BJ'}
          <SwitchButton
            bgClassName='ml-2'
            buttonSize='sm'
            checked={isBjLive}
            disabled={isOnEditing}
            onChange={() => {
              toggleIsBjLive();
            }}
          />
        </div>
      </section>
      <section className='flex '>
        <div className='flex-1'>
          {data?.pages?.length === 0 && (
            <NotFoundDisplay
              text={'회원님이 PICK한 콘텐츠가 없어요.\n방송을 시청하고 나만의 PICK을 만들어 보세요.'}
              content={
                <Button
                  className='mt-9 h-12 px-[30px] text-colorFg12 hover:underline'
                  onClick={() => {
                    router.push('/live');
                  }}
                >
                  {'방송시청하기'}
                </Button>
              }
            />
          )}
          {data?.pages && data?.pages?.length > 0 && (
            <PickUserList>
              {data.pages.map((item) => {
                return (
                  <PickUserItem
                    key={item.userIdx}
                    title={getRankTitle(item.rank)}
                    isKingFan={item.rank === 1}
                    item={item}
                    isOnEditing={isOnEditing}
                    isChecked={checkedIdxList.includes(item.userIdx)}
                    onCheckedChange={() => {
                      setCheckedIdxList((prev) => {
                        if (prev.includes(item.userIdx)) {
                          return prev.filter((index) => index !== item.userIdx);
                        }
                        return [...prev, item.userIdx];
                      });
                    }}
                  />
                );
              })}
            </PickUserList>
          )}
          <div className='h-5 w-full' ref={observerRef} />
        </div>
      </section>
    </>
  );
}

export default FanPickContainer;
